import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Trophy, Heart, Coins, Users, ArrowLeft, Plus, Crown, Clock, Star, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import TeamsStrip from "@/components/plyx/TeamsStrip";

const PlyxChallengeDetail = () => {
  const { id } = useParams();
  const { user } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hearting, setHearting] = useState(null);
  const [joining, setJoining] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // iter-119n: host-side finalists picker — fetches the audition pool
  // (host-only endpoint) and lets the host toggle which entries advance.
  const [finalistsModalOpen, setFinalistsModalOpen] = useState(false);
  const [auditionQueue, setAuditionQueue] = useState(null);
  const [pickedFinalists, setPickedFinalists] = useState(new Set());
  const [savingFinalists, setSavingFinalists] = useState(false);

  const reload = () => {
    setLoading(true);
    plyxApi.get(`/plyx/challenges/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load challenge"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const openVideoPicker = async () => {
    if (!user) { toast.error("Sign in first"); return; }
    setPickerOpen(true);
    if (myVideos.length === 0) {
      try {
        const { data: r } = await plyxApi.get(`/plyx/u/${user.handle}`);
        setMyVideos(r?.videos || []);
      } catch (e) {
        toast.error("Could not load your videos");
      }
    }
  };

  const submitEntry = async (video_id) => {
    setJoining(true);
    try {
      await plyxApi.post(`/plyx/challenges/${id}/join`, { video_id });
      toast.success("You're in! Rally your fans 🎯");
      setPickerOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const heartEntry = async (entry_id) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    setHearting(entry_id);
    try {
      const { data: r } = await plyxApi.post(`/plyx/challenges/${id}/entries/${entry_id}/heart`);
      setData((d) => d && {
        ...d,
        entries: d.entries.map((e) => e.entry_id === entry_id ? {
          ...e,
          viewer_hearted: r.hearted,
          hearts_count: e.hearts_count + (r.hearted ? 1 : -1),
        } : e),
        total_hearts: d.total_hearts + (r.hearted ? 1 : -1),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed");
    } finally {
      setHearting(null);
    }
  };

  // iter-119n: open finalists picker (host-only)
  const openFinalistsPicker = async () => {
    setFinalistsModalOpen(true);
    setAuditionQueue(null);
    try {
      const { data: r } = await plyxApi.get(`/plyx/challenges/${id}/audition-queue`);
      setAuditionQueue(r);
      setPickedFinalists(new Set(r.finalist_entry_ids || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load audition queue");
      setFinalistsModalOpen(false);
    }
  };

  const toggleFinalist = (entry_id) => {
    setPickedFinalists((prev) => {
      const next = new Set(prev);
      if (next.has(entry_id)) {
        next.delete(entry_id);
      } else {
        const slots = auditionQueue?.finals_slots || 0;
        if (slots > 0 && next.size >= slots) {
          toast.message(`You've picked the max ${slots} finalists. Untick one first.`);
          return prev;
        }
        next.add(entry_id);
      }
      return next;
    });
  };

  const submitFinalists = async () => {
    if (pickedFinalists.size === 0) {
      toast.error("Pick at least one finalist.");
      return;
    }
    setSavingFinalists(true);
    try {
      await plyxApi.post(`/plyx/challenges/${id}/select-finalists`, {
        entry_ids: Array.from(pickedFinalists),
      });
      toast.success(`${pickedFinalists.size} finalist${pickedFinalists.size === 1 ? "" : "s"} locked in. Finals are live!`);
      setFinalistsModalOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not lock in finalists");
    } finally {
      setSavingFinalists(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="plyx-challenge-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
      </div>
    );
  }
  if (!data) return null;

  const endsAt = data.ends_at ? new Date(data.ends_at) : null;
  const timeLeftMs = endsAt ? endsAt - Date.now() : 0;
  const myEntry = data.entries.find((e) => e.creator?.plyx_user_id === user?.plyx_user_id);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-challenge-detail-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        {data.banner_url && (
          <img src={plyxMediaUrl(data.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"/>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.95))" }}/>
        <div className="relative max-w-4xl mx-auto px-5 py-10">
          <Link to="/plyx/challenges" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4" data-testid="plyx-challenge-detail-back">
            <ArrowLeft className="w-4 h-4"/> all challenges
          </Link>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold">{data.template}</span>
            {data.status === "live" && (
              <span className="px-2.5 py-1 rounded-full bg-red-600 text-[10px] uppercase font-bold animate-pulse inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white"/> LIVE
              </span>
            )}
            {/* iter-119n: Phase pill — only shown for "audition_finals"
                format challenges. Makes it obvious whether we're in
                Auditions or Finals so viewers know what they're voting
                on. */}
            {data.format === "audition_finals" && data.phase === "auditions" && (
              <span className="px-2.5 py-1 rounded-full bg-[#FFB42D] text-black text-[10px] uppercase font-bold inline-flex items-center gap-1" data-testid="plyx-challenge-phase-auditions">
                <Users className="w-3 h-3"/> Auditions
              </span>
            )}
            {data.format === "audition_finals" && data.phase === "finals" && (
              <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FF2D7A] text-black text-[10px] uppercase font-bold inline-flex items-center gap-1" data-testid="plyx-challenge-phase-finals">
                <Star className="w-3 h-3 fill-black"/> Finals
              </span>
            )}
            {data.status === "ended" && (
              <span className="px-2.5 py-1 rounded-full bg-black/60 border border-[#FFD700]/40 text-[10px] uppercase font-bold inline-flex items-center gap-1">
                <Trophy className="w-3 h-3 text-[#FFD700]"/> Ended
              </span>
            )}
            {data.country_tags?.map((cc) => (
              <span key={cc} className="px-2 py-0.5 rounded-full bg-white/10 text-xs font-semibold">{cc}</span>
            ))}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-tight">{data.title}</h1>
          {data.description && (
            <p className="text-sm text-white/75 mt-3 whitespace-pre-wrap max-w-2xl">{data.description}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-white/60"/> {data.entry_count} {data.entry_count === 1 ? "entry" : "entries"}</span>
            <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#FF2D7A]"/> {data.total_hearts}</span>
            {data.prize_pool_coins > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[#FFB42D] font-semibold">
                <Coins className="w-4 h-4"/> {data.prize_pool_coins.toLocaleString()} prize
              </span>
            )}
            {data.status === "live" && timeLeftMs > 0 && (
              <span className="inline-flex items-center gap-1.5 text-white/70">
                <Clock className="w-4 h-4"/>
                {Math.floor(timeLeftMs / (1000 * 60 * 60 * 24)) > 0
                  ? `${Math.floor(timeLeftMs / (1000 * 60 * 60 * 24))}d left`
                  : `${Math.floor(timeLeftMs / (1000 * 60 * 60))}h left`}
              </span>
            )}
            <span className="ml-auto text-white/60 text-xs">by @{data.creator_handle}</span>
          </div>

          {data.status === "live" && user && !myEntry && (
            <button
              type="button"
              onClick={openVideoPicker}
              data-testid="plyx-challenge-join-btn"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm"
              style={{ background: "#E85D4E" }}
            >
              <Plus className="w-4 h-4"/> Enter this challenge
            </button>
          )}
          {myEntry && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10/15 border border-[#00E0C7]/30 text-white/85 text-xs font-semibold" data-testid="plyx-challenge-myentry-badge">
              <Trophy className="w-3.5 h-3.5"/> You&apos;re entry #{myEntry.rank} · {myEntry.hearts_count} ❤
            </div>
          )}

          {/* iter-119n: Host-only "Pick Finalists" CTA — only shown
              for "audition_finals" challenges that are still in the
              auditions phase. Once finalists are locked in, this
              disappears and the phase pill flips to "Finals". */}
          {user
            && data.format === "audition_finals"
            && data.phase === "auditions"
            && user.plyx_user_id === data.creator_plyx_user_id && (
            <button
              type="button"
              onClick={openFinalistsPicker}
              data-testid="plyx-challenge-pick-finalists-btn"
              className="mt-4 ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-black text-sm shadow-lg"
              style={{ background: "linear-gradient(135deg, #FFD700 0%, #FFB42D 100%)" }}
            >
              <Star className="w-4 h-4 fill-black"/> Pick {data.finals_slots > 0 ? `${data.finals_slots} ` : ""}finalists
            </button>
          )}
        </div>
      </div>

      {/* Video picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[75] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-3xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Pick a video to enter</h3>
            {myVideos.length === 0 ? (
              <div className="text-white/60 text-sm">
                You have no videos yet.{" "}
                <Link to="/plyx/upload" className="text-[#FFB42D] underline">Upload one</Link>.
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {myVideos.map((v) => (
                  <button
                    key={v.video_id}
                    type="button"
                    disabled={joining}
                    onClick={() => submitEntry(v.video_id)}
                    data-testid={`plyx-challenge-pick-video-${v.video_id}`}
                    className="aspect-[9/16] rounded-lg overflow-hidden bg-black/50 relative border border-white/10 hover:border-[#FFB42D] disabled:opacity-50"
                  >
                    {v.thumbnail_url
                      ? <img src={plyxMediaUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
                      : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">no thumb</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* iter-119n: Finalists picker (host-only). Shows the entire
          audition pool sorted by current score. Host taps to toggle.
          Counter shows X/N picked. "Lock in" promotes them to finals. */}
      {finalistsModalOpen && (
        <div className="fixed inset-0 z-[75] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={() => !savingFinalists && setFinalistsModalOpen(false)}>
          <div className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 rounded-3xl flex flex-col max-h-[92vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-5 border-b border-white/10">
              <div>
                <h3 className="font-display text-xl">Pick your finalists</h3>
                <p className="text-xs text-white/55 mt-1">
                  Tap entries to select. Auditions stay archived but their hearts/gifts carry over.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !savingFinalists && setFinalistsModalOpen(false)}
                data-testid="plyx-finalists-close"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              >
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {!auditionQueue ? (
                <div className="py-16 text-center text-white/50 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2"/>
                  Loading audition queue…
                </div>
              ) : auditionQueue.entries.length === 0 ? (
                <div className="py-16 text-center text-white/50 text-sm">
                  No entries yet. Share your challenge link first.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="plyx-finalists-grid">
                  {auditionQueue.entries.map((e) => {
                    const picked = pickedFinalists.has(e.entry_id);
                    return (
                      <button
                        key={e.entry_id}
                        type="button"
                        onClick={() => toggleFinalist(e.entry_id)}
                        data-testid={`plyx-finalist-card-${e.entry_id}`}
                        className={`relative aspect-[9/16] rounded-xl overflow-hidden border-2 transition-all text-left ${
                          picked ? "border-[#FFD700] ring-2 ring-[#FFD700]/30" : "border-white/10 hover:border-white/40"
                        }`}
                      >
                        {e.thumbnail_url
                          ? <img src={plyxMediaUrl(e.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover"/>
                          : <div className="absolute inset-0 bg-black/50"/>}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"/>
                        {picked && (
                          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#FFD700] flex items-center justify-center shadow-lg">
                            <CheckCircle2 className="w-5 h-5 text-black"/>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-2.5 text-white">
                          <div className="font-semibold text-xs truncate">@{e.creator.handle}</div>
                          <div className="text-[10px] text-white/70 mt-0.5 inline-flex items-center gap-2">
                            <span className="inline-flex items-center gap-0.5"><Heart className="w-2.5 h-2.5 text-[#FF2D7A]"/> {e.hearts_count}</span>
                            {e.gift_coins > 0 && <span className="inline-flex items-center gap-0.5 text-[#FFB42D]"><Coins className="w-2.5 h-2.5"/> {e.gift_coins}</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-white/10 flex items-center gap-3">
              <div className="text-sm text-white/70 flex-1">
                <strong className="text-white">{pickedFinalists.size}</strong>
                {auditionQueue?.finals_slots ? ` / ${auditionQueue.finals_slots}` : ""}
                {" "}picked
              </div>
              <button
                type="button"
                onClick={submitFinalists}
                disabled={savingFinalists || pickedFinalists.size === 0}
                data-testid="plyx-finalists-confirm"
                className="px-5 py-2.5 rounded-full font-bold text-black text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #FFD700 0%, #FFB42D 100%)" }}
              >
                {savingFinalists
                  ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin"/> Locking in…</span>
                  : "Lock in finalists →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="max-w-4xl mx-auto px-5 py-8 pb-24">
        <TeamsStrip
          challengeId={data.challenge_id}
          isLive={data.status === "live"}
          isOpen={data.status === "live" || data.status === "open"}
        />
        <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FFB42D]"/> {data.status === "ended" ? "Champions" : "Leaderboard"}
        </h2>
        {data.entries.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center text-white/60" data-testid="plyx-challenge-no-entries">
            No entries yet — be the first.
          </div>
        ) : (
          <div className="space-y-3" data-testid="plyx-challenge-leaderboard">
            {data.entries.map((e) => (
              <EntryRow
                key={e.entry_id}
                entry={e}
                onHeart={() => heartEntry(e.entry_id)}
                hearting={hearting === e.entry_id}
                challengeStatus={data.status}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EntryRow = ({ entry, onHeart, hearting, challengeStatus }) => {
  const top3 = entry.rank <= 3;
  const rankColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  return (
    <div
      data-testid={`plyx-challenge-entry-${entry.entry_id}`}
      className={`flex items-center gap-3 p-3 rounded-2xl border ${top3 ? "border-[#FFB42D]/40 bg-[#FFB42D]/5" : "border-white/10 bg-white/5"}`}
    >
      <div className="font-display text-2xl w-9 text-center" style={{ color: top3 ? rankColors[entry.rank - 1] : "rgba(255,255,255,0.4)" }}>
        {top3 ? <Crown className="w-6 h-6 mx-auto"/> : entry.rank}
      </div>
      <Link to={`/plyx/v/${entry.video_id}`} className="w-14 h-20 rounded-lg overflow-hidden bg-black shrink-0">
        {entry.thumbnail_url
          ? <img src={plyxMediaUrl(entry.thumbnail_url)} alt="" className="w-full h-full object-cover"/>
          : <div className="w-full h-full flex items-center justify-center text-white/20 text-[10px]">video</div>}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/plyx/u/${entry.creator.handle}`} className="font-semibold text-sm truncate block hover:underline">
          @{entry.creator.handle}
        </Link>
        {entry.creator.country && (
          <div className="text-[10px] text-white/40 uppercase tracking-wider">{entry.creator.country}</div>
        )}
        {entry.caption && (
          <div className="text-xs text-white/60 line-clamp-1 mt-0.5">{entry.caption}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-base">{entry.hearts_count} ❤</div>
        {entry.gift_coins > 0 && (
          <div className="text-[10px] text-[#FFB42D]">{entry.gift_coins.toLocaleString()} coins</div>
        )}
      </div>
      {challengeStatus !== "ended" && (
        <button
          type="button"
          onClick={onHeart}
          disabled={hearting}
          data-testid={`plyx-challenge-heart-${entry.entry_id}`}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${
            entry.viewer_hearted ? "bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white" : "bg-white/10 hover:bg-white/20 text-white"
          }`}
          aria-label="Heart vote"
        >
          {hearting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Heart className={`w-4 h-4 ${entry.viewer_hearted ? "fill-white" : ""}`}/>}
        </button>
      )}
    </div>
  );
};

export default PlyxChallengeDetail;
